import { useState } from 'react';
import { MuellIcon, PfeilLinksIcon, PfeilRechtsIcon, PlusIcon } from '../icons';
import type { Protokoll, Schluessel as SchluesselEintrag } from '../domain/types';

interface SchluesselProps {
	protokoll: Protokoll;
	onAendern: (protokoll: Protokoll) => void;
	onWeiter: () => void;
	onZurueck: () => void;
}

const VORSCHLAEGE = ['Wohnungsschlüssel', 'Haustürschlüssel', 'Kellerschlüssel', 'Briefkastenschlüssel', 'Garage', 'Tiefgarage'];

export function Schluessel({ protokoll, onAendern, onWeiter, onZurueck }: SchluesselProps) {
	const [eigeneArt, setEigeneArt] = useState('');

	/**
	 * Ein Vorschlag-Chip verschwindet aus der Leiste, sobald seine Art schon
	 * erfasst ist (siehe `vorhandeneVorschlaege` unten) — ein zweiter Klick auf
	 * dieselbe Art kann über die Chips also nie passieren. Die Anzahl danach
	 * zu erhöhen ist Aufgabe des +/−-Stellers am Eintrag, nicht dieser Funktion.
	 */
	function hinzufuegen(art: string) {
		const neuer: SchluesselEintrag = { id: crypto.randomUUID(), art, anzahl: 1 };
		onAendern({ ...protokoll, schluessel: [...protokoll.schluessel, neuer] });
	}

	function aktualisieren(id: string, update: Partial<SchluesselEintrag>) {
		onAendern({
			...protokoll,
			schluessel: protokoll.schluessel.map((s) => (s.id === id ? { ...s, ...update } : s))
		});
	}

	function entfernen(id: string) {
		onAendern({ ...protokoll, schluessel: protokoll.schluessel.filter((s) => s.id !== id) });
	}

	function anzahlAendern(id: string, delta: number) {
		const eintrag = protokoll.schluessel.find((s) => s.id === id);
		if (!eintrag) return;
		const neueAnzahl = eintrag.anzahl + delta;
		if (neueAnzahl <= 0) {
			entfernen(id);
			return;
		}
		aktualisieren(id, { anzahl: neueAnzahl });
	}

	const vorhandeneVorschlaege = VORSCHLAEGE.filter(
		(name) => !protokoll.schluessel.some((s) => s.art.toLowerCase() === name.toLowerCase())
	);

	function eigeneHinzufuegen() {
		const bereinigt = eigeneArt.trim();
		if (!bereinigt) return;
		hinzufuegen(bereinigt);
		setEigeneArt('');
	}

	return (
		<div className="schritt schritt--schluessel">
			<div className="schritt-kopf">
				<h2>Schlüsselübergabe</h2>
				<p className="schritt-erklaerung">Jede Art antippen, um sie hinzuzufügen — die Anzahl lässt sich danach in der Liste anpassen.</p>
			</div>

			<div className="chip-leiste">
				{vorhandeneVorschlaege.map((name) => (
					<button key={name} type="button" className="chip chip--hinzufuegen" onClick={() => hinzufuegen(name)}>
						<PlusIcon width={14} height={14} />
						{name}
					</button>
				))}
			</div>

			<div className="manuell-eingabe">
				<input
					value={eigeneArt}
					onChange={(e) => setEigeneArt(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && eigeneHinzufuegen()}
					placeholder="Eigene Bezeichnung, z. B. Waschküche"
				/>
				<button type="button" onClick={eigeneHinzufuegen}>
					Hinzufügen
				</button>
			</div>

			{protokoll.schluessel.length === 0 && (
				<p className="hinweis">Noch keine Schlüssel erfasst. Oben eine Art antippen.</p>
			)}

			<ul className="schluessel-liste">
				{protokoll.schluessel.map((s) => (
					<li key={s.id} className="schluessel-eintrag">
						<span className="schluessel-name">{s.art}</span>
						<div className="stepper">
							<button type="button" onClick={() => anzahlAendern(s.id, -1)} aria-label="Weniger">
								−
							</button>
							<span className="stepper-wert">{s.anzahl}</span>
							<button type="button" onClick={() => anzahlAendern(s.id, 1)} aria-label="Mehr">
								+
							</button>
						</div>
						<button type="button" className="icon-knopf" onClick={() => entfernen(s.id)} aria-label="Eintrag entfernen">
							<MuellIcon width={16} height={16} />
						</button>
					</li>
				))}
			</ul>

			<footer className="schritt-fuss">
				<button type="button" className="nebenknopf" onClick={onZurueck}>
					<PfeilLinksIcon width={18} height={18} />
					Zurück
				</button>
				<button type="button" className="hauptknopf hauptknopf--mit-icon" onClick={onWeiter}>
					Weiter
					<PfeilRechtsIcon width={20} height={20} />
				</button>
			</footer>
		</div>
	);
}
